import type { components } from "@majitap/sdk";
import { formatMoney } from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { useCompanyFormat } from "../../company";
import { ORDER_STATUS, PAYMENT_STATUS } from "../../labels";
import styles from "./record.module.css";

type AdminOrder = components["schemas"]["AdminOrder"];

/** The deliveries that still count: a rerouted one is replaced, a cancelled one won't happen. */
export const liveDeliveries = (o: Pick<AdminOrder, "deliveries">) =>
  o.deliveries.filter((d) => d.status !== "rerouted" && d.status !== "cancelled");

/**
 * The order at a glance (`Console / Order / Redesign`): its status and payment, the total, what's
 * paid and still to collect, the margin, and how many deliveries are done. Every amount comes from
 * the API; nothing is summed here.
 */
export function Figures({ order: o, termName }: { order: AdminOrder; termName: string | null }) {
  const format = useCompanyFormat();
  const [statusLabel, statusTone] = ORDER_STATUS[o.status];
  const [paymentLabel, paymentTone] = PAYMENT_STATUS[o.payment_status];
  const live = liveDeliveries(o);
  const delivered = live.filter((d) => d.status === "delivered").length;
  const moving = live.length - delivered;
  const owing = o.due_amount !== "0.00" && o.status !== "cancelled";
  return (
    <section className={styles.figures} aria-label="The order at a glance">
      <div className={styles.figure}>
        <span className={styles.figureLabel}>Status</span>
        <span className={styles.badges}>
          <Badge tone={statusTone}>{statusLabel}</Badge>
          <Badge tone={paymentTone}>{paymentLabel}</Badge>
          {o.postpaid ? <Badge tone="info">Postpaid</Badge> : null}
        </span>
      </div>
      <Figure
        label="Total"
        value={formatMoney(o.total_amount, format)}
        sub={live.length === 1 ? "1 delivery" : `${live.length} deliveries`}
      />
      <Figure label="Paid" value={formatMoney(o.paid_amount, format)} sub={termName ?? "No payment term"} />
      <Figure
        label="To collect"
        value={formatMoney(o.due_amount, format)}
        sub={owing ? "Still owed" : "Nothing owed"}
        tone={owing ? "warning" : undefined}
      />
      <Figure
        label="Margin"
        value={formatMoney(o.margin, format)}
        sub={`Costs ${formatMoney(o.costs_total, format)}`}
        tone="success"
      />
      <div className={styles.figure}>
        <span className={styles.figureLabel}>Deliveries</span>
        <span className={styles.figureValue}>
          {delivered} of {live.length} delivered
        </span>
        <span className={styles.bar} aria-hidden>
          {live.map((d, i) => (
            <span
              key={d.id}
              className={i < delivered ? styles.segDone : i < delivered + moving ? styles.segMoving : ""}
            />
          ))}
        </span>
      </div>
    </section>
  );
}

function Figure({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: "warning" | "success";
}) {
  return (
    <div className={styles.figure}>
      <span className={styles.figureLabel}>{label}</span>
      <span className={styles.figureValue} data-tone={tone}>
        {value}
      </span>
      <span className={styles.small}>{sub}</span>
    </div>
  );
}
